import { Lifecycle } from '@shared/models/lifecycles.model';

/** Identifier for a high-level lifecycle stage grouping shown in the scope selector. */
export type LifecycleGroupId = 'embodied' | 'operation' | 'endOfLife';

export type LifecyclePhaseSlot = {
  /** Short label shown in the phase toggle (e.g. "A1-A3", "B3", "C1"). */
  label: string;
  /** One-line description shown in the hover tooltip. */
  description: string;
  /**
   * Underlying toggleable lifecycle, or `null` when the phase is not yet supported
   * by the data model. Unsupported phases render disabled (greyed) per the design.
   */
  lifecycle: Lifecycle | null;
};

export type LifecycleGroupDefinition = {
  id: LifecycleGroupId;
  label: string;
  /** One-line description shown in the hover tooltip on the group label. */
  description: string;
  phases: LifecyclePhaseSlot[];
};

export type LifecycleGroup = LifecycleGroupDefinition & {
  /** Phases with a supported {@link Lifecycle} enum value (excludes disabled placeholders). */
  toggleableLifecycles: Lifecycle[];
};

const getToggleableLifecycles = (
  phases: readonly LifecyclePhaseSlot[],
): Lifecycle[] =>
  phases.flatMap((phase) =>
    phase.lifecycle !== null ? [phase.lifecycle] : [],
  );

/**
 * Lifecycle phases grouped into Embodied / Operation / End-of-Life for the scope selector.
 * Phases whose {@link LifecyclePhaseSlot.lifecycle} is `null` (B3, B5, D) are not
 * in the {@link Lifecycle} enum yet and are shown disabled to mirror the Figma layout.
 */
const LIFECYCLE_GROUP_DEFINITIONS: readonly LifecycleGroupDefinition[] = [
  {
    id: 'embodied',
    label: 'Embodied',
    description:
      'Impacts from material production, transport to site and the construction process.',
    phases: [
      {
        label: 'A1-A3',
        description: 'A1-A3 – Product stage',
        lifecycle: Lifecycle.A1A3,
      },
      {
        label: 'A4',
        description: 'A4 – Transport to site',
        lifecycle: Lifecycle.A4,
      },
      {
        label: 'A5',
        description: 'A5 – Site Installation',
        lifecycle: Lifecycle.A5,
      },
    ],
  },
  {
    id: 'operation',
    label: 'Operation',
    description:
      'Impacts during the building’s operation, including maintenance, replacements, energy and water use.',
    phases: [
      { label: 'B1', description: 'B1 – Use', lifecycle: Lifecycle.B1 },
      { label: 'B2', description: 'B2 – Maintenance', lifecycle: Lifecycle.B2 },
      { label: 'B3', description: 'B3 – Repairs', lifecycle: null },
      {
        label: 'B4',
        description: 'B4 – Replacements',
        lifecycle: Lifecycle.B4,
      },
      { label: 'B5', description: 'B5 – Refurbishments', lifecycle: null },
      { label: 'B6', description: 'B6 – Energy use', lifecycle: Lifecycle.B6 },
      { label: 'B7', description: 'B7 – Water use', lifecycle: Lifecycle.B7 },
    ],
  },
  {
    id: 'endOfLife',
    label: 'End-of-Life',
    description:
      'Impacts from demolition, transport, waste processing and disposal at the end of the lifecycle.',
    phases: [
      {
        label: 'C1',
        description: 'C1 – Deconstruction and demolition',
        lifecycle: Lifecycle.C1,
      },
      {
        label: 'C2',
        description: 'C2 – Transport to waste processing',
        lifecycle: Lifecycle.C2,
      },
      {
        label: 'C3-C4',
        description: 'C3-C4 – Waste processing and disposal',
        lifecycle: Lifecycle.C3C4,
      },
      {
        label: 'D',
        description: 'D – Reuse, recovery and recycling potential',
        lifecycle: null,
      },
    ],
  },
];

export const LIFECYCLE_GROUPS: readonly LifecycleGroup[] =
  LIFECYCLE_GROUP_DEFINITIONS.map((group) => ({
    ...group,
    toggleableLifecycles: getToggleableLifecycles(group.phases),
  }));
